import { Layout } from "providers/Layout";
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  Input
} from "shared/components";
import { InputSize } from "shared/components/forms/DateInput";
import {
  AlertCircle,
  CheckDone01,
  Upload01,
  UserRight01
} from "untitledui-js-base";
import { AccountStatusVariant, PaymentStatuses } from "./constants";
import { Link } from "react-router-dom";
import { SubscribersTable } from "./components/SubscribersTable";
import { Tabs } from "shared/components/Tabs";
import { ReportModal } from "./components/ReportModal";
import { Pagination } from "shared/components/Pagination";
import { CSVUploadBanner } from "./components/CSVUploadBanner";
import { useAuthContext } from "providers/Auth";
import { useCombinedRentReportingController } from "./controllers";

export const RentReportingView = () => {
  const {
    subscribersMeta,
    needToReportCountMeta,
    pendingCountMeta,
    searchParams,
    setSearchParams,
    debouncedParams,
    reportAllRenters,
    sorted,
    setSorted,
    isReportingAllRenters,
    isReportingSingleRenter,
    reportSingleRenter
  } = useCombinedRentReportingController();
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectDefaultValue, setSelectDefaultValue] = useState<PaymentStatuses>(
    PaymentStatuses.NO_DATA
  );
  const [selectValues, setSelectValues] = useState<
    { id: number; action: string | number }[]
  >([]);

  const { type } = useAuthContext();

  const [showPendingIDV, setShowPendingIDV] = useState(true);
  const [showPendingOptIn, setShowPendingOptIn] = useState(true);
  const [showPendingOptOut, setShowPendingOptOut] = useState(true);

  useEffect(() => {
    if (
      type.isSDK ||
      (needToReportCountMeta.data === 0 &&
        searchParams.type === AccountStatusVariant.NeedToReport)
    )
      setSearchParams({ ...searchParams, type: AccountStatusVariant.All });
  }, [type.isSDK, needToReportCountMeta.data]);

  const paidOnTime = useMemo(() => {
    if (selectValues.length === 0)
      return selectDefaultValue === PaymentStatuses.PAID_ON_TIME
        ? needToReportCountMeta.data
        : 0;
    else
      return selectDefaultValue === PaymentStatuses.PAID_ON_TIME
        ? needToReportCountMeta.data - selectValues.length
        : selectValues.filter((v) => v.action === PaymentStatuses.PAID_ON_TIME)
            .length || 0;
  }, [selectValues, selectDefaultValue, needToReportCountMeta.data]);

  const handleReportAll = () => {
    const verifications = selectValues.map((item) => ({
      ...item,
      action:
        item.action === PaymentStatuses.PAID_ON_TIME
          ? PaymentStatuses.PAID_ON_TIME
          : item.action === PaymentStatuses.NO_DATA
            ? PaymentStatuses.NO_DATA
            : PaymentStatuses.DELINQUENCY,
      months_delinquency:
        item.action === PaymentStatuses.PAID_ON_TIME ||
        item.action === PaymentStatuses.NO_DATA
          ? undefined
          : item.action
    }));
    reportAllRenters({ verifications, defaultAction: selectDefaultValue });
    setSelectValues([]);
    setShowReportModal(false);
  };

  const isNeedToReportTab =
    searchParams.type === AccountStatusVariant.NeedToReport;
  const isAllTab = searchParams.type === AccountStatusVariant.All;
  const isPendingTab = searchParams.type === AccountStatusVariant.Pending;

  const hasNeedToReport = needToReportCountMeta.data > 0;
  const hasPending = pendingCountMeta.data > 0;

  const hasSubscribers =
    Array.isArray(subscribersMeta.data?.items) &&
    subscribersMeta.data.items.length > 0;

  const filteredItems = useMemo(() => {
    const items = subscribersMeta.data?.items;
    if (!Array.isArray(items)) return [];

    if (!isPendingTab && !isAllTab) return items;

    return items.filter((item) => {
      if (isAllTab && item.type !== "pending") return true;

      const isOptIn = item.case_status === "pending_opt_in";
      const isOptOut = item.case_status === "pending_opt_out";
      const isIDV = [
        "identity_verification_pending",
        "identity_verification_rejected",
        "identity_verification_skipped"
      ].includes(item.identity_verification_status);

      return (
        (showPendingOptIn && isOptIn) ||
        (showPendingOptOut && isOptOut) ||
        (showPendingIDV && !isOptIn && !isOptOut && isIDV)
      );
    });
  }, [
    isPendingTab,
    isAllTab,
    subscribersMeta.data?.items,
    showPendingIDV,
    showPendingOptIn,
    showPendingOptOut
  ]);

  if (subscribersMeta.isError)
    return (
      <Layout>
        <div>
          <h1 className="mb-4 text-lg font-medium">
            Error loading subscribers.
          </h1>
          <p className="text-base font-medium">
            {subscribersMeta.error.message}
          </p>
        </div>
      </Layout>
    );

  return (
    <Layout
      headerClassName="py-0"
      header={
        <>
          <div className="px-6 py-4 text-xl font-bold border-b border-alpha-100 text-content-1000 flex justify-between items-center">
            <header>Rent reporting</header>
            {type.isManual ||
            (type.isPMS && type.isBoomOps) ||
            (type.isPMS && type.canManageEntitiesDirectly) ? (
              <div className="flex gap-2">
                <Link to="/rent-reporting/add">
                  <Button
                    Icon={UserRight01}
                    className="stroke-content-300"
                    size={ButtonSize.SM}
                  >
                    Subscribe manually
                  </Button>
                </Link>
                <Link to="/rent-reporting/upload">
                  <Button
                    Icon={Upload01}
                    size={ButtonSize.SM}
                    className="stroke-content-300"
                  >
                    Import data
                  </Button>
                </Link>
              </div>
            ) : null}
          </div>
          <CSVUploadBanner />
          {needToReportCountMeta.data > 0 && !type.isSDK ? (
            <div className="flex items-center gap-6 border-b border-alpha-100 bg-background-1000 px-6 py-5">
              <AlertCircle size="24" className="stroke-primary-500" />
              <div className="h-10 w-px bg-alpha-40" />
              <div>
                <p className="text-sm font-medium text-content-400">
                  Please review and report the status of rent payments.
                </p>
                <p className="text-sm font-medium text-content-400">
                  Past months will be reported as &apos;No Data&apos; if you
                  don&apos;t report rent payments within 30 days
                </p>
              </div>
            </div>
          ) : null}
        </>
      }
    >
      <div className="flex justify-between items-center mb-2">
        <Input
          id="search"
          label=""
          placeholder="Search"
          value={searchParams.search}
          onChange={(e) =>
            setSearchParams({
              ...searchParams,
              page: "1",
              search: e.target.value
            })
          }
          className="!mb-0 max-w-[271px]"
          size={InputSize.SM}
        />

        {!type.isSDK &&
          (isNeedToReportTab || isAllTab) &&
          hasNeedToReport &&
          hasSubscribers && (
            <div className="flex items-center gap-4">
              <Button
                variant={ButtonVariant.TEXT}
                size={ButtonSize.XS}
                onClick={() => {
                  const status =
                    selectDefaultValue === PaymentStatuses.NO_DATA
                      ? PaymentStatuses.PAID_ON_TIME
                      : PaymentStatuses.NO_DATA;
                  setSelectDefaultValue(status);
                  setSelectValues([]);
                }}
              >
                {selectDefaultValue === PaymentStatuses.NO_DATA
                  ? "Mark all as paid on-time"
                  : "Mark all as no data"}
              </Button>
              <div className="h-10 w-px bg-alpha-40" />
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                Icon={CheckDone01}
                onClick={() => setShowReportModal(true)}
                loading={isReportingAllRenters}
              >
                Report all
              </Button>
            </div>
          )}
      </div>

      <div className="flex justify-between items-center mb-4">
        <Tabs
          tabs={[
            ...(type.isSDK ? [] : [AccountStatusVariant.NeedToReport]),
            AccountStatusVariant.Active,
            AccountStatusVariant.Pending,
            AccountStatusVariant.Unsubscribed,
            AccountStatusVariant.All
          ]}
          displayMap={{
            [AccountStatusVariant.All]: "All",
            [AccountStatusVariant.Unsubscribed]: "Unsubscribed",
            [AccountStatusVariant.Active]: "Active",
            [AccountStatusVariant.Pending]: (
              <>
                Pending{" "}
                {(pendingCountMeta.data || 0) > 0 && (
                  <div className="flex items-center justify-center rounded bg-danger-500 text-[10px] text-background-1000 px-2">
                    {pendingCountMeta.data}
                  </div>
                )}
              </>
            ),
            [AccountStatusVariant.NeedToReport]: (
              <>
                Need to report{" "}
                {(needToReportCountMeta.data || 0) > 0 && (
                  <div className="flex items-center justify-center rounded bg-danger-500 text-[10px] text-background-1000 px-2">
                    {needToReportCountMeta.data}
                  </div>
                )}
              </>
            )
          }}
          selected={searchParams.type}
          setActiveTab={(t) => {
            setSearchParams({
              ...searchParams,
              page: "1",
              type: t
            });
          }}
        />

        {!type.isSDK && (isPendingTab || isAllTab) && hasPending && (
          <div className="flex items-start gap-6">
            {subscribersMeta.data.items.some(
              (item) => item.case_status === "pending_opt_in"
            ) ? (
              <Checkbox
                id="pending-opt-in"
                label="Pending opt in"
                checked={showPendingOptIn}
                onChange={(e) => setShowPendingOptIn(e.target.checked)}
              />
            ) : null}

            {subscribersMeta.data.items.some(
              (item) => item.case_status === "pending_opt_out"
            ) ? (
              <Checkbox
                id="pending-opt-out"
                label="Pending opt out"
                checked={showPendingOptOut}
                onChange={(e) => setShowPendingOptOut(e.target.checked)}
              />
            ) : null}

            {subscribersMeta.data.items.some(
              (item) =>
                item.case_status != "pending_opt_in" &&
                item.case_status != "pending_opt_out"
            )
              ? subscribersMeta.data.items.some((item) =>
                  [
                    "identity_verification_pending",
                    "identity_verification_rejected",
                    "identity_verification_skipped"
                  ].includes(item.identity_verification_status)
                ) && (
                  <Checkbox
                    id="pending-idv"
                    label="Pending IDV"
                    checked={showPendingIDV}
                    onChange={(e) => setShowPendingIDV(e.target.checked)}
                  />
                )
              : null}
          </div>
        )}
      </div>

      <SubscribersTable
        rentersData={filteredItems}
        hasSearchParams={!!debouncedParams}
        selectDefaultValue={selectDefaultValue}
        selectValues={selectValues}
        setSelectValues={setSelectValues}
        isLoading={subscribersMeta.isFetching || isReportingAllRenters}
        sorted={sorted}
        setSorted={setSorted}
        activeTab={searchParams.type as AccountStatusVariant}
        isReportingSingleRenter={isReportingSingleRenter}
        reportSingleRenter={reportSingleRenter}
      />
      <div className="mt-10 flex items-center">
        <p className="text-sm text-content-300 font-medium w-full">
          {subscribersMeta.data?.total_count}{" "}
          {subscribersMeta.data?.total_count === 1 ? "renter" : "renters"}
          {searchParams.type === AccountStatusVariant.NeedToReport ? (
            <>
              , (<span className="font-bold">{paidOnTime || 0}</span> paid
              on-time,{" "}
              <span className="font-bold">
                {needToReportCountMeta.data - paidOnTime || 0}
              </span>{" "}
              late payment)
            </>
          ) : null}
        </p>
        <Pagination
          className="justify-end"
          total={subscribersMeta.data?.page_count as number}
          current={Number(searchParams.page)}
          onChange={(e) =>
            setSearchParams({ ...searchParams, page: String(e) })
          }
        />
      </div>
      <ReportModal
        opened={showReportModal}
        onClose={() => setShowReportModal(false)}
        onReportAll={handleReportAll}
        paidOnTime={paidOnTime}
        needToReportCount={needToReportCountMeta.data}
      />
    </Layout>
  );
};
